'use client';

import * as React from 'react';
import { cn } from '../../lib/cn';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  /** Grow to fit content instead of showing a scrollbar. */
  autoGrow?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid = false, autoGrow = false, rows = 3, onInput, disabled, ...rest },
  ref,
) {
  const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

  const setRefs = (node: HTMLTextAreaElement | null) => {
    innerRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
  };

  const handleInput: NonNullable<TextareaProps['onInput']> = (e) => {
    if (autoGrow && innerRef.current) {
      innerRef.current.style.height = 'auto';
      innerRef.current.style.height = `${innerRef.current.scrollHeight}px`;
    }
    onInput?.(e);
  };

  return (
    <textarea
      ref={setRefs}
      rows={rows}
      disabled={disabled}
      onInput={handleInput}
      className={cn(
        'w-full rounded-control border bg-surface px-[var(--control-px)] py-[var(--space-2-5)]',
        'font-sans text-base leading-normal text-fg outline-none placeholder:text-fg-muted',
        'transition-[border-color,box-shadow] duration-[var(--duration-fast)]',
        'focus:border-accent focus:shadow-[var(--ring)]',
        'disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60',
        autoGrow ? 'resize-none' : 'resize-y',
        invalid ? 'border-danger focus:border-danger' : 'border-border-strong',
        className,
      )}
      {...rest}
    />
  );
});
